// The Whole Story – seed travels along the path, path draws, labels pop in.
// Modes: scroll (linked to page scroll), play (once when visible), none (static).
if (!customElements.get('twst-seed-story')) {
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const ease = (v) => (v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2);

  customElements.define(
    'twst-seed-story',
    class TwstSeedStory extends HTMLElement {
      connectedCallback() {
        this.mode = this.dataset.mode;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.mode = 'none';
        this.seed = this.querySelector('[data-seed]');
        this.labels = [...this.querySelectorAll('[data-at]')];
        this.paths = [...this.querySelectorAll('[data-path]')];
        this.progress = this.mode === 'none' ? 1 : 0;
        this.render = this.render.bind(this);
        this.onScroll = () => {
          if (!this.ticking) {
            this.ticking = true;
            requestAnimationFrame(() => {
              this.ticking = false;
              this.fromScroll();
            });
          }
        };

        if (this.mode === 'scroll') {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.fromScroll();
        } else if (this.mode === 'play') {
          this.io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
              this.io.disconnect();
              this.play(Number(this.dataset.duration || 8) * 1000);
            }
          }, { threshold: 0.25 });
          this.io.observe(this);
          this.render();
        } else {
          this.render();
        }
        window.addEventListener('resize', this.render, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        window.removeEventListener('resize', this.render);
        this.io?.disconnect();
        cancelAnimationFrame(this.frame);
      }

      fromScroll() {
        const r = this.getBoundingClientRect();
        const vh = window.innerHeight;
        // starts when the scene top reaches 75% of the viewport, ends when its bottom is fully visible
        this.progress = clamp((vh * 0.75 - r.top) / Math.max(1, r.height - vh * 0.25));
        this.render();
      }

      play(duration) {
        const start = performance.now();
        const step = (now) => {
          this.progress = ease(clamp((now - start) / duration));
          this.render();
          if (this.progress < 1) this.frame = requestAnimationFrame(step);
        };
        this.frame = requestAnimationFrame(step);
      }

      activePath() {
        return this.paths.find((p) => p.getClientRects().length);
      }

      render() {
        const p = this.progress;
        // Figma timeline: seed scales in first (0–6%), then travels along the path
        const scale = ease(clamp(p / 0.06));
        const travel = clamp((p - 0.1) / 0.9);

        this.style.setProperty('--draw', travel.toFixed(4));
        this.labels.forEach((l) => l.classList.toggle('is-on', p * 100 >= Number(l.dataset.at) - 0.01));

        const path = this.activePath();
        if (!path || !this.seed) return;
        const len = path.getTotalLength();
        const ctm = path.getScreenCTM();
        if (!ctm) return;
        const pt = path.getPointAtLength(len * travel).matrixTransform(ctm);
        const box = this.getBoundingClientRect();
        this.seed.style.setProperty('--seed-x', `${(pt.x - box.left).toFixed(1)}px`);
        this.seed.style.setProperty('--seed-y', `${(pt.y - box.top).toFixed(1)}px`);
        this.seed.style.setProperty('--seed-s', this.mode === 'none' ? 1 : scale.toFixed(3));
      }
    }
  );
}
